import React, { useState, useEffect } from "react";
import {
  Button,
  Row,
  Col,
  Card,
  Form,
  Container,
  FormControl,
  Table,
  Modal,
  Alert,
} from "react-bootstrap";
import PerfectScrollbar from "react-perfect-scrollbar";
import { db, auth } from "../firebaseConfig";
import {
  collection,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  query,
  where,
  doc,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";

export default function CreateInvoices() {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [invoices, setInvoices] = useState([]);
  const [newInvoice, setNewInvoice] = useState({
    items: [],
    additionalCosts: 0,
    vatRate: 12.5,
    notes: "",
  });
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedInvoiceForModal, setSelectedInvoiceForModal] = useState(null);

  // Fetch user role and permissions
  const fetchUserRoleAndPermissions = async (uid) => {
    try {
      const userDocRef = doc(db, "Users", uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        const role = userDoc.data().role;
        setUserRole(role);

        if (role === "admin") {
          setPermissions({ canCreateInvoices: true }); // Admin always has access
          setAccessDenied(false);
          return;
        }

        const roleDocRef = doc(db, "Roles", role);
        const roleDoc = await getDoc(roleDocRef);

        if (roleDoc.exists()) {
          const rolePermissions = roleDoc.data().permissions;
          setPermissions(rolePermissions);

          if (!rolePermissions.canCreateInvoices) {
            setAccessDenied(true);
          } else {
            setAccessDenied(false);
          }
        } else {
          console.error("Role document not found.");
          setAccessDenied(true);
        }
      } else {
        console.error("User document not found.");
        setAccessDenied(true);
      }
    } catch (error) {
      console.error("Error fetching user role and permissions:", error);
      setAccessDenied(true);
    }
  };

  // Fetch users
  const fetchAllUsers = async () => {
    try {
      const usersSnapshot = await getDocs(collection(db, "Users"));
      const userList = usersSnapshot.docs.map((doc) => ({
        email: doc.data().email,
        fullName: doc.data().fullName || "Unknown User",
      }));
      setUsers(userList);
      setFilteredUsers(userList);
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  };

  // Fetch invoices
  const fetchInvoices = async (userEmail = null) => {
    try {
      const invoicesRef = collection(db, "Invoices");
      const invoicesQuery = userEmail
        ? query(invoicesRef, where("user.email", "==", userEmail))
        : invoicesRef;
      const invoicesSnapshot = await getDocs(invoicesQuery);

      const invoiceList = invoicesSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setInvoices(invoiceList);
    } catch (err) {
      console.error("Error fetching invoices:", err);
    }
  };

  // Add a new item to the invoice
  const addItem = () => {
    setNewInvoice((prev) => ({
      ...prev,
      items: [...prev.items, { name: "", description: "", unitCost: 0, quantity: 1, cost: 0 }],
    }));
  };

  const handleEdit = (invoice) => {
    setEditingInvoice(invoice);
    setNewInvoice({
      items: invoice.items,
      additionalCosts: invoice.additionalCosts,
      vatRate: invoice.vatRate,
      notes: invoice.notes,
    });
    setSelectedUser(invoice.user);
  };

  // Open modal with selected invoice details
  const handleModalOpen = (invoice) => {
    setSelectedInvoiceForModal(invoice);
    setShowModal(true);
  };


  // Handle changes to invoice items
  const handleItemChange = (index, field, value) => {
    setNewInvoice((prev) => {
      const updatedItems = [...prev.items];
      updatedItems[index][field] = value;

      if (field === "unitCost" || field === "quantity") {
        updatedItems[index].cost = Number(updatedItems[index].unitCost) * Number(updatedItems[index].quantity);
      }

      return { ...prev, items: updatedItems };
    });
  };

  // Save invoice
  const saveInvoice = async () => {
    if (!selectedUser) {
      alert("Please select a user for the invoice.");
      return;
    }

    if (newInvoice.items.length === 0) {
      alert("Invoice must have at least one item.");
      return;
    }

    try {
      const subTotal = newInvoice.items.reduce((sum, item) => sum + item.cost, 0);
      const vat = (subTotal * newInvoice.vatRate) / 100;
      const totalCost = subTotal + vat + parseFloat(newInvoice.additionalCosts || 0);

      const invoiceData = {
        user: selectedUser,
        items: newInvoice.items,
        additionalCosts: newInvoice.additionalCosts,
        vatRate: newInvoice.vatRate,
        notes: newInvoice.notes,
        totalCost,
        createdAt: new Date(),
      };

      if (editingInvoice) {
        const invoiceRef = doc(db, "Invoices", editingInvoice.id);
        await updateDoc(invoiceRef, invoiceData);
        alert("Invoice updated successfully!");
        setEditingInvoice(null);
      } else {
        const invoicesRef = collection(db, "Invoices");
        await addDoc(invoicesRef, invoiceData);
        alert("Invoice created successfully!");
      }

      setNewInvoice({ items: [], additionalCosts: 0, vatRate: 12.5, notes: "" });
      fetchInvoices(selectedUser.email);
    } catch (err) {
      console.error("Error saving invoice:", err);
      alert("Failed to save invoice.");
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    const lowerCaseQuery = query.toLowerCase();
    const filtered = users.filter(
      (user) =>
        user.fullName.toLowerCase().includes(lowerCaseQuery) ||
        user.email.toLowerCase().includes(lowerCaseQuery)
    );
    setFilteredUsers(filtered);
  };

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    fetchInvoices(user.email);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      if (authUser) {
        setUser(authUser);
        await fetchUserRoleAndPermissions(authUser.uid);
        fetchAllUsers();
      } else {
        setUser(null);
        setPermissions(null);
        setAccessDenied(true);
        setFilteredUsers([]);
        setInvoices([]);
      }
    });

    return () => unsubscribe();
  }, []);

  return (
    <React.Fragment>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        {accessDenied ? (
          <div className="bg-gradient-danger text-white rounded-4 p-4 shadow-lg">
            <div className="d-flex align-items-center">
              <i className="ri-error-warning-line fs-4 me-3"></i>
              <div>
                <h6 className="mb-0 fw-bold">Access Denied</h6>
                <p className="mb-0">You do not have permission to create invoices.</p>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Enhanced Header Section */}
            <div className="text-center mb-5">
              <div className="d-flex align-items-center justify-content-center mb-3">
                <i className="ri-file-add-line fs-1 text-primary me-3"></i>
                <h1 className="display-6 fw-bold text-primary mb-0">Create Invoices</h1>
              </div>
              <p className="lead text-muted">Create and manage invoices for employees</p>
            </div>

            {/* Enhanced User Selection Card */}
            <Card className="border-0 shadow-lg rounded-4 mb-5">
              <div className="bg-gradient-primary text-white p-4 rounded-top-4" 
                   style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                <div className="d-flex align-items-center">
                  <i className="ri-user-search-line fs-4 me-3"></i>
                  <h5 className="mb-0 fw-bold">Select Employee</h5>
                </div>
              </div>
              <Card.Body className="p-4">
                <FormControl
                  type="text"
                  placeholder="Search employees..."
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="form-control-lg shadow-sm border-2 mb-3"
                  style={{ borderRadius: '12px' }}
                />
                <PerfectScrollbar style={{ maxHeight: "200px", overflow: "auto" }}>
                  <div className="list-group">
                    {filteredUsers.map((user, index) => (
                      <div
                        key={index}
                        className={`list-group-item list-group-item-action rounded-3 mb-2 border-0 ${
                          selectedUser === user ? "active bg-primary" : "bg-light"
                        }`}
                        onClick={() => handleUserSelect(user)}
                        style={{ 
                          cursor: "pointer",
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          if (selectedUser !== user) {
                            e.currentTarget.style.backgroundColor = '#e9ecef';
                            e.currentTarget.style.transform = 'translateX(4px)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (selectedUser !== user) {
                            e.currentTarget.style.backgroundColor = '#f8f9fa';
                            e.currentTarget.style.transform = 'translateX(0)';
                          }
                        }}
                      >
                        <div className="d-flex align-items-center">
                          <div className="bg-primary bg-opacity-10 rounded-circle p-2 me-3">
                            <i className="ri-user-line text-primary"></i>
                          </div>
                          <div>
                            <h6 className="mb-0 fw-semibold">{user.fullName}</h6>
                            <small className="text-muted">{user.email}</small>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </PerfectScrollbar>
              </Card.Body>
            </Card>

            {selectedUser && (
              <>
                {/* Enhanced Invoice Creation Form */}
                <Card className="border-0 shadow-lg rounded-4 mb-5">
                  <div className="bg-gradient-success text-white p-4 rounded-top-4" 
                       style={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' }}>
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center">
                        <i className="ri-file-text-line fs-4 me-3"></i>
                        <h5 className="mb-0 fw-bold">Invoice Details</h5>
                      </div>
                      <span className="badge bg-white text-success px-3 py-2 rounded-pill">
                        {newInvoice.items.length} items
                      </span>
                    </div>
                  </div>
                  <Card.Body className="p-4">
                    <div className="table-responsive">
                      <Table className="table-hover table-borderless">
                        <thead className="bg-primary text-white">
                          <tr>
                            <th className="border-0">
                              <i className="ri-shopping-bag-line me-2"></i>Item Name
                            </th>
                            <th className="border-0">
                              <i className="ri-file-text-line me-2"></i>Description
                            </th>
                            <th className="border-0">
                              <i className="ri-money-dollar-circle-line me-2"></i>Unit Cost
                            </th>
                            <th className="border-0">
                              <i className="ri-number-1 me-2"></i>Quantity
                            </th>
                            <th className="border-0">
                              <i className="ri-calculator-line me-2"></i>Cost
                            </th>
                            <th className="border-0">
                              <i className="ri-settings-3-line me-2"></i>Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {newInvoice.items.map((item, index) => (
                            <tr key={index}
                                style={{ 
                                  transition: 'all 0.3s ease'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#f8f9fa';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = '';
                                }}>
                              <td>
                                <Form.Control
                                  type="text"
                                  value={item.name}
                                  onChange={(e) => handleItemChange(index, "name", e.target.value)}
                                  className="form-control-sm border-2"
                                  style={{ borderRadius: '8px' }}
                                  placeholder="Item name"
                                />
                              </td>
                              <td>
                                <Form.Control
                                  type="text"
                                  value={item.description}
                                  onChange={(e) => handleItemChange(index, "description", e.target.value)}
                                  className="form-control-sm border-2"
                                  style={{ borderRadius: '8px' }}
                                  placeholder="Description"
                                />
                              </td>
                              <td>
                                <Form.Control
                                  type="number"
                                  value={item.unitCost}
                                  onChange={(e) => handleItemChange(index, "unitCost", e.target.value)}
                                  className="form-control-sm border-2"
                                  style={{ borderRadius: '8px' }}
                                  placeholder="0.00"
                                />
                              </td>
                              <td>
                                <Form.Control
                                  type="number"
                                  value={item.quantity}
                                  onChange={(e) => handleItemChange(index, "quantity", e.target.value)}
                                  className="form-control-sm border-2"
                                  style={{ borderRadius: '8px' }}
                                  placeholder="1"
                                />
                              </td>
                              <td>
                                <span className="fw-bold text-success">${item.cost.toFixed(2)}</span>
                              </td>
                              <td>
                                <Button
                                  variant="danger"
                                  size="sm"
                                  onClick={() =>
                                    setNewInvoice((prev) => ({
                                      ...prev,
                                      items: prev.items.filter((_, i) => i !== index),
                                    }))
                                  }
                                  className="rounded-pill"
                                >
                                  <i className="ri-delete-bin-line"></i>
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                    
                    <div className="text-center mb-4">
                      <Button 
                        variant="success" 
                        onClick={addItem} 
                        className="px-4 py-2 rounded-pill fw-semibold"
                      >
                        <i className="ri-add-line me-2"></i>Add Item
                      </Button>
                    </div>

                    <Row className="g-4">
                      <Col md="4">
                        <Form.Group>
                          <Form.Label className="fw-semibold">
                            <i className="ri-add-line me-2"></i>Additional Costs
                          </Form.Label>
                          <Form.Control
                            type="number"
                            value={newInvoice.additionalCosts}
                            onChange={(e) =>
                              setNewInvoice((prev) => ({ ...prev, additionalCosts: parseFloat(e.target.value) }))
                            }
                            className="form-control-lg shadow-sm border-2"
                            style={{ borderRadius: '12px' }}
                            placeholder="0.00"
                          />
                        </Form.Group>
                      </Col>
                      <Col md="4">
                        <Form.Group>
                          <Form.Label className="fw-semibold">
                            <i className="ri-percent-line me-2"></i>VAT Rate (%)
                          </Form.Label>
                          <Form.Control
                            type="number"
                            value={newInvoice.vatRate}
                            onChange={(e) =>
                              setNewInvoice((prev) => ({ ...prev, vatRate: parseFloat(e.target.value) }))
                            }
                            className="form-control-lg shadow-sm border-2"
                            style={{ borderRadius: '12px' }}
                            placeholder="12.5"
                          />
                        </Form.Group>
                      </Col>
                      <Col md="4">
                        <Form.Group>
                          <Form.Label className="fw-semibold">
                            <i className="ri-file-text-line me-2"></i>Notes
                          </Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            value={newInvoice.notes}
                            onChange={(e) =>
                              setNewInvoice((prev) => ({ ...prev, notes: e.target.value }))
                            }
                            className="shadow-sm border-2"
                            style={{ borderRadius: '12px' }}
                            placeholder="Additional notes..."
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <div className="text-center mt-4">
                      <Button 
                        variant="primary" 
                        size="lg"
                        onClick={saveInvoice} 
                        className="px-5 py-3 rounded-pill fw-semibold shadow-lg"
                        style={{ 
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'scale(1.05)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'scale(1)';
                        }}
                      >
                        <i className="ri-save-line me-2"></i>
                        {editingInvoice ? "Update Invoice" : "Save Invoice"}
                      </Button>
                    </div>
                  </Card.Body>
                </Card>

                {/* Enhanced Existing Invoices Section */}
                <Card className="border-0 shadow-lg rounded-4">
                  <div className="bg-gradient-info text-white p-4 rounded-top-4" 
                       style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center">
                        <i className="ri-file-list-3-line fs-4 me-3"></i>
                        <h5 className="mb-0 fw-bold">Existing Invoices</h5>
                      </div>
                      <span className="badge bg-white text-primary px-3 py-2 rounded-pill">
                        {invoices.length} invoices
                      </span>
                    </div>
                  </div>
                  <Card.Body className="p-0">
                    {invoices.length > 0 ? (
                      <div className="table-responsive">
                        <Table className="table-hover table-borderless mb-0">
                          <thead className="bg-primary text-white">
                            <tr>
                              <th className="border-0">
                                <i className="ri-hashtag me-2"></i>#
                              </th>
                              <th className="border-0">
                                <i className="ri-file-text-line me-2"></i>Invoice ID
                              </th>
                              <th className="border-0">
                                <i className="ri-calendar-line me-2"></i>Created At
                              </th>
                              <th className="border-0">
                                <i className="ri-money-dollar-circle-line me-2"></i>Total Cost
                              </th>
                              <th className="border-0">
                                <i className="ri-file-text-line me-2"></i>Notes
                              </th>
                              <th className="border-0">
                                <i className="ri-settings-3-line me-2"></i>Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {invoices.map((invoice, index) => (
                              <tr key={index}
                                  style={{ 
                                    cursor: "pointer",
                                    transition: 'all 0.3s ease'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#f8f9fa';
                                    e.currentTarget.style.transform = 'scale(1.01)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = '';
                                    e.currentTarget.style.transform = 'scale(1)';
                                  }}>
                                <td className="fw-semibold">{index + 1}</td>
                                <td className="fw-semibold text-primary">{invoice.id}</td>
                                <td className="fw-semibold">{new Date(invoice.createdAt.seconds * 1000).toLocaleDateString()}</td>
                                <td>
                                  <span className="fw-bold text-success">${invoice.totalCost.toFixed(2)}</span>
                                </td>
                                <td className="text-truncate" style={{ maxWidth: '200px' }} title={invoice.notes}>
                                  {invoice.notes || 'No notes'}
                                </td>
                                <td>
                                  <div className="d-flex gap-2">
                                    <Button 
                                      variant="info" 
                                      size="sm"
                                      onClick={() => handleModalOpen(invoice)}
                                      className="rounded-pill"
                                    >
                                      <i className="ri-eye-line me-1"></i>View
                                    </Button>
                                    <Button 
                                      variant="warning" 
                                      size="sm"
                                      onClick={() => handleEdit(invoice)}
                                      className="rounded-pill"
                                    >
                                      <i className="ri-edit-line me-1"></i>Edit
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    ) : (
                      <div className="text-center py-5">
                        <div className="mb-4">
                          <i className="ri-file-list-3-line text-muted" style={{ fontSize: '4rem' }}></i>
                        </div>
                        <h5 className="text-muted mb-3">No Invoices Found</h5>
                        <p className="text-muted">No invoices have been created for this employee yet.</p>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </>
            )}
          </>
        )}

        {/* Enhanced Modal for Viewing Invoice */}
        <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
          <Modal.Header className="bg-gradient-primary text-white border-0">
            <Modal.Title className="fw-bold">
              <i className="ri-file-text-line me-2"></i>
              Invoice Details
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            {selectedInvoiceForModal && (
              <>
                <div className="bg-primary bg-opacity-10 rounded-3 p-4 text-center mb-4">
                  <h4 className="text-primary fw-bold mb-2">Invoice #{selectedInvoiceForModal.id}</h4>
                  <p className="text-muted mb-0">
                    Created on {new Date(selectedInvoiceForModal.createdAt.seconds * 1000).toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </p>
                </div>

                {selectedInvoiceForModal.notes && (
                  <Card className="border-0 bg-light rounded-3 mb-4">
                    <Card.Body className="p-3">
                      <h6 className="fw-bold text-primary mb-2">
                        <i className="ri-file-text-line me-2"></i>Notes
                      </h6>
                      <p className="mb-0">{selectedInvoiceForModal.notes}</p>
                    </Card.Body>
                  </Card>
                )}

                <div className="table-responsive">
                  <Table className="table-hover table-borderless">
                    <thead className="bg-primary text-white">
                      <tr>
                        <th className="border-0">
                          <i className="ri-hashtag me-2"></i>#
                        </th>
                        <th className="border-0">
                          <i className="ri-shopping-bag-line me-2"></i>Item Name
                        </th>
                        <th className="border-0">
                          <i className="ri-file-text-line me-2"></i>Description
                        </th>
                        <th className="border-0">
                          <i className="ri-number-1 me-2"></i>Quantity
                        </th>
                        <th className="border-0">
                          <i className="ri-money-dollar-circle-line me-2"></i>Unit Cost
                        </th>
                        <th className="border-0">
                          <i className="ri-calculator-line me-2"></i>Total
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedInvoiceForModal.items.map((item, index) => (
                        <tr key={index}
                            style={{ 
                              transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#f8f9fa';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = '';
                            }}>
                          <td className="fw-semibold">{index + 1}</td>
                          <td className="fw-semibold">{item.name}</td>
                          <td className="text-muted">{item.description}</td>
                          <td>
                            <span className="badge bg-primary rounded-pill">{item.quantity}</span>
                          </td>
                          <td className="fw-semibold">${Number(item.unitCost).toFixed(2)}</td>
                          <td>
                            <span className="fw-bold text-success">${(Number(item.unitCost) * Number(item.quantity)).toFixed(2)}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                <Card className="border-0 bg-gradient-light rounded-3 mt-4">
                  <Card.Body className="p-4">
                    <div className="d-flex justify-content-between align-items-center">
                      <h5 className="fw-bold text-primary mb-0">
                        <i className="ri-calculator-line me-2"></i>Total Amount
                      </h5>
                      <h4 className="fw-bold text-success mb-0">
                        ${selectedInvoiceForModal.totalCost.toFixed(2)}
                      </h4>
                    </div>
                  </Card.Body>
                </Card>
              </>
            )}
          </Modal.Body>
          <Modal.Footer className="border-0 p-4">
            <Button variant="secondary" onClick={() => setShowModal(false)} className="px-4 py-2 rounded-pill fw-semibold">
              <i className="ri-close-line me-2"></i>Close
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
      <Footer />
    </React.Fragment>
  );
}
